import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Drawer,
  Form,
  Button,
  SelectPicker,
  Input,
  Message,
} from 'rsuite';

interface LeaveTypeFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onFiltersChange: (filters: any) => void;
  onResetFilters: () => void;
  currentFilters: {
    type: string;
    category: string;
    increment: string;
    typeBalance: string;
  };
  toaster: any;
}

const LeaveTypeFilterDrawer: React.FC<LeaveTypeFilterDrawerProps> = ({
  open,
  onClose,
  onFiltersChange,
  onResetFilters,
  currentFilters,
  toaster
}) => {
  const [formValue, setFormValue] = React.useState(currentFilters);
  const { t } = useTranslation();

  const categoryOptions = [
    { label: t('ALL'), value: '' },
    { label: t('Exceptional'), value: 'exceptional' },
    { label: t('Regular'), value: 'regular' },
    { label: t('Paid'), value: 'paid' },
    { label: t('Unpaid'), value: 'unpaid' }
  ];

  const booleanOptions = [
    { label: t('ALL'), value: '' },
    { label: t('YES'), value: 'true' },
    { label: t('NO'), value: 'false' }
  ];

  const typeBalanceOptions = [
    { label: t('ALL'), value: '' },
    { label: t('Leave Balance'), value: 'leavebalance' },
    { label: t('Sick Balance'), value: 'sickBalance' }

  ];

  const handleSubmit = () => {
    const hasFilters = Object.values(formValue).some(value => value !== '' && value !== null);
    
    if (hasFilters) {
      onFiltersChange({
        type: formValue.type,
        category: formValue.category,
        increment: formValue.increment,
        typeBalance: formValue.typeBalance
      });
      toaster.push(
        <Message type="success" duration={3000}>
          {t('Filters applied successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
    } else {
      onResetFilters();
    }
    onClose();
  };

  const handleReset = () => {
    setFormValue({
      type: '',
      category: '',
      increment: '',
      typeBalance: ''
    });
    onResetFilters();
    toaster.push(
      <Message type="info" duration={3000}>
        {t('Filters have been reset!')}
      </Message>,
      { placement: 'topCenter' }
    );
    onClose();
  };

  useEffect(() => {
    if (open) {
      setFormValue(currentFilters);
    }
  }, [open, currentFilters]);

  return (
    <Drawer 
      backdrop="static" 
      size="sm" 
      placement="right" 
      onClose={onClose} 
      open={open}
    >
      <Drawer.Header>
        <Drawer.Title>{t('Filter Leave Types')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleReset} appearance="subtle">
            {t('Reset')}
          </Button>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Apply')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('Leave Type')}</Form.ControlLabel>
            <Form.Control 
              name={t("type")} 
              accepter={Input}
              placeholder={t("Enter leave type")}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Category')}</Form.ControlLabel>
            <Form.Control 
              name={t("category")}
              accepter={SelectPicker}
              data={categoryOptions}
              searchable={false}
              placeholder={t("Select category")}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Increment')}</Form.ControlLabel>
            <Form.Control 
              name={t("increment")}
              accepter={SelectPicker}
              data={booleanOptions}
              searchable={false}
              placeholder={t("Select increment status")}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Type Balance')}</Form.ControlLabel>
            <Form.Control 
              name={t("typeBalance")}
              accepter={SelectPicker}
              data={typeBalanceOptions}
              searchable={false}
              placeholder={t("Select type balance")}
              block
            />
          </Form.Group>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default LeaveTypeFilterDrawer;